import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import { BAND_LABEL, formatGrade, gradeBand, GRADE_NAME, QUALITY_AUDITS, type GradeKind } from '../../audit/grades';
import {
  CAUSE_DRAG,
  CAUSE_LABEL,
  CAUSES,
  LOST_TO,
  LOST_TO_LABEL,
  lostByWho,
  shownLost,
  type LostTo,
  type Cause,
  type DistrictBreakdown,
} from '../../audit/gradeBreakdown';
import { PARTY_AUDIT_LABEL, type Party } from '../../audit/requirements';
import { useAppUser } from '../../auth/AppUserContext';
import { WINDOW_LABELS } from '../../audit/windows';
import { overdueStoresText, type GradeBreakdown, type StoreRow } from '../../data/dashboard';
import { lookupDisplayName, PersonName } from '../PersonName';
import { formatDay } from '../dates';
import { DistrictLink, StoreLink } from '../entityLinks';
import { seesPeoplePerformance } from '../../data/scope';

/**
 * "Why this grade?" (owner ask 2026-10-18; math in src/audit/gradeBreakdown.ts;
 * two grades since 2026-10-19). Every grade chip is a button that opens this:
 * TIMELINESS — its two parts as points lost, the biggest drag in words, and
 * the late audits / overdue tasks behind them; QUALITY — the audits it
 * averages. Anchored to the chip on desktop; a bottom sheet on phones (CSS,
 * ≤720px).
 */

/** Fixed cause → color (dataviz palette slots 1, 2), never cycled. */
export const CAUSE_COLOR: Record<Cause, string> = {
  onTime: '#2a78d6',
  tasks: '#eb6834',
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
/** Points lost as shown: whole when whole ("−60", never "−60.0"), else one decimal. */
const lostText = (n: number) => String(Number(n.toFixed(1)));

/** Heading note for a part: "lost 3 points" / "no points lost" (DM run: "(0)" read like a score of zero). */
const pts = (n: number) => (n > 0 ? `lost ${n} point${n === 1 ? '' : 's'}` : 'no points lost');

/** Chip-as-button + popover shell. */
export function GradePopover({
  chipClass,
  chipText,
  label,
  title,
  children,
}: {
  chipClass: string;
  chipText: string;
  /** Accessible name of the button (grade + parts). */
  label: string;
  title: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Phones get a bottom sheet (CSS): no anchoring, and page scroll/resize
  // (the URL bar collapsing) mustn't close it.
  const isSheet = () => window.matchMedia('screen and (max-width: 720px)').matches;

  const close = (focusChip: boolean) => {
    setOpen(false);
    if (focusChip) btn.current?.focus();
  };

  // Position under the chip (flipped above / clamped at the edges); fixed
  // positioning, so tables that scroll sideways can't clip it.
  useLayoutEffect(() => {
    if (!open || !btn.current || !pop.current) return;
    if (isSheet()) {
      setPos({ top: 0, left: 0 });
      return;
    }
    const r = btn.current.getBoundingClientRect();
    const w = pop.current.offsetWidth;
    const h = pop.current.offsetHeight;
    const left = Math.max(8, Math.min(r.left, window.innerWidth - w - 8));
    const below = r.bottom + 6;
    const above = r.top - h - 6;
    // Below if it fits, else above if that fits, else clamped on screen
    // (the popover scrolls inside itself; page scroll would close it).
    const top =
      below + h <= window.innerHeight - 8
        ? below
        : above >= 8
          ? above
          : Math.max(8, Math.min(below, window.innerHeight - h - 8));
    setPos({ top, left });
  }, [open]);

  // Focus once placed — a visibility:hidden element can't take focus.
  useEffect(() => {
    if (open && pos) pop.current?.focus({ preventScroll: true });
  }, [open, pos]);

  // Next open re-measures (hidden until placed).
  useEffect(() => {
    if (!open) setPos(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!pop.current?.contains(t) && !btn.current?.contains(t)) close(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close(true);
    };
    // Scrolling the page moves the chip away from a fixed popover — close it
    // (scrolling INSIDE the popover / sheet is fine).
    const onScroll = (e: Event) => {
      if (!isSheet() && !pop.current?.contains(e.target as Node)) close(false);
    };
    const onResize = () => {
      if (!isSheet()) close(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btn}
        type="button"
        className={`${chipClass} grade-chip-btn`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${label}. Show why`}
        title="Why this grade?"
        onClick={() => (open ? close(false) : setOpen(true))}
      >
        {chipText}
      </button>
      {open &&
        createPortal(
          <>
            <div className="grade-pop-scrim" aria-hidden="true" />
            <div
              ref={pop}
              className="grade-pop"
              role="dialog"
              aria-labelledby={titleId}
              tabIndex={-1}
              style={pos ? (isSheet() ? undefined : { top: pos.top, left: pos.left }) : { visibility: 'hidden', top: 0, left: 0 }}
            >
              <div className="grade-pop-head">
                <strong id={titleId}>{title}</strong>
                <button type="button" className="grade-pop-close" aria-label="Close" onClick={() => close(true)}>
                  ✕
                </button>
              </div>
              {children}
            </div>
          </>,
          document.body,
        )}
    </>
  );
}

/** The two timeliness parts as bars: points lost out of each part's weight. */
function LostBars({ lost, weights, grade }: { lost: Record<Cause, number>; weights: Record<Cause, number>; grade: number }) {
  const shown = shownLost(lost, grade);
  return (
    <ul className="grade-pop-bars">
      {CAUSES.filter((c) => weights[c] > 0).map((c) => (
        <li key={c}>
          <span className="grade-pop-bar-label">{CAUSE_LABEL[c]}</span>
          <span className="grade-pop-track" aria-hidden="true">
            <span
              className="grade-pop-fill"
              style={{ width: `${Math.min(100, (100 * lost[c]) / weights[c])}%`, background: CAUSE_COLOR[c] }}
            />
          </span>
          <span className="grade-pop-bar-value">
            <span className="muted">lost </span>
            <strong>{shown[c]}</strong>
            <span className="muted"> of {Math.round(weights[c] * 10) / 10}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Store teams see "the DM", never a name, in grade breakdowns (owner ruling 2026-10-28). */
function responsible(row: StoreRow, party: Party, names: boolean): string {
  if (party === 'auditor') return 'Auditors';
  if (party === 'store') return `the store team (${row.store.name.trim() || row.store.storeNumber})`;
  if (!names) return 'the DM';
  return row.store.dmName.trim() || row.store.dmUpn.trim() || 'no DM set for this district';
}

/** On screen, the DM by their directory name (PersonName), never a bare mailbox alias (UI review U26). */
function ResponsibleName({ row, party, names }: { row: StoreRow; party: Party; names: boolean }) {
  if (party === 'dm' && names && (row.store.dmUpn ?? '').trim()) return <PersonName upn={row.store.dmUpn} />;
  return <>{responsible(row, party, names)}</>;
}

/** Names of DMs in grade breakdowns: staff only (owner ruling 2026-10-28); fails closed. */
function useShowsNames(): boolean {
  const { user } = useAppUser();
  return seesPeoplePerformance(user?.role);
}

/** One plain sentence naming what pulls timeliness down most. */
export function storeDragSentence(row: StoreRow, b: GradeBreakdown, names: boolean): string {
  switch (b.biggest) {
    case 'onTime': {
      const worst = [...b.clocks].sort((x, y) => y.lost - x.lost)[0]!;
      const when =
        worst.status === 'overdue'
          ? `is ${plural(worst.daysOverdue, 'day')} overdue`
          : `is ${WINDOW_LABELS[worst.status].toLowerCase()}`;
      return `Mostly audits not on time: ${worst.ref.template.name} (${PARTY_AUDIT_LABEL[worst.party]}, ${responsible(row, worst.party, names)}) ${when}.`;
    }
    case 'tasks': {
      const overdue = b.tasks.filter((t) => t.overdue).length;
      return `Mostly open tasks: ${plural(overdue, 'overdue task')}.`;
    }
    default:
      return 'Nothing is making this store late.';
  }
}

/** At most this many directory lookups per popover (forged rows can't fan out Graph calls). */
const MAX_NAME_LOOKUPS = 50;

/** Display names for task assignees (shared per-page-load cache in ui/PersonName). */
function useDisplayNames(upns: readonly string[]): Map<string, string> {
  const [names, setNames] = useState<Map<string, string>>(new Map());
  const key = [...new Set(upns.map((u) => u.trim().toLowerCase()).filter(Boolean))].sort().join('|');
  useEffect(() => {
    let live = true;
    const list = key ? key.split('|').slice(0, MAX_NAME_LOOKUPS) : [];
    void Promise.all(
      list.map(async (u) => {
        return [u, await lookupDisplayName(u)] as const;
      }),
    ).then((pairs) => {
      if (live) setNames(new Map(pairs.filter((p): p is readonly [string, string] => !!p[1])));
    });
    return () => {
      live = false;
    };
  }, [key]);
  return names;
}

function TimelinessExplain({ row }: { row: StoreRow }) {
  const showsNames = useShowsNames();
  const b = row.breakdown;
  const shown = shownLost(b.lost, b.grade.timeliness!);
  const storeId = Number(row.store.id);
  const costly = b.tasks.filter((t) => t.lost > 0).sort((x, y) => y.lost - x.lost);
  // A task filed against ANOTHER store (one not on this board) counts here
  // when it is assigned to this store's account — but the viewer may not be
  // allowed to see that store, so it shows as a count only, never its title
  // or link (security.md "Phase 6a, Task counts").
  const own = costly.filter((t) => t.ref.storeId === storeId);
  const foreign = costly.filter((t) => t.ref.storeId !== storeId);
  const foreignLost = foreign.reduce((n, t) => n + t.lost, 0);
  const onTimeTasks = b.tasks.length - costly.length;
  const account = row.store.managerEmail.trim().toLowerCase();
  const names = useDisplayNames(own.map((t) => t.ref.assignedTo).filter((u) => u.trim().toLowerCase() !== account));
  const assignee = (upn: string) => {
    const u = upn.trim().toLowerCase();
    if (!u) return 'nobody';
    if (u === account) return `${row.store.name} store account`;
    return names.get(u) ?? upn;
  };
  return (
    <>
      <p className="grade-pop-drag">{storeDragSentence(row, b, showsNames)}</p>
      <p className="muted">Timeliness is 100 minus the points lost in each part:</p>
      <LostBars lost={b.lost} weights={b.weights} grade={b.grade.timeliness!} />
      <WhoLine row={row} />

      <h4>
        Audits on time <span className="muted">({pts(shown.onTime)})</span>
      </h4>
      {/* Owner ruling 2026-10-26: a DM's or Auditor's late audit DOES count against the store — and the breakdown says so, apart from the store's own tasks. */}
      {b.clocks.length > 0 && (
        <p className="muted grade-pop-note">
          {b.clocks.some((c) => c.party !== 'store')
            ? 'Each audit is owed by the DM, the Auditors or the store team; when one is late, it lowers this store’s grade.'
            : 'The store team’s own audits.'}
        </p>
      )}
      <ul className="grade-pop-facts">
        {b.clocks.map((c, i) => (
          <li key={i}>
            <span>
              {c.ref.template.name} · {PARTY_AUDIT_LABEL[c.party]}
              <span className="muted"> — <ResponsibleName row={row} party={c.party} names={showsNames} /></span>
            </span>
            {c.points === null ? (
              <span className="muted">Not started (no effect until its first audit)</span>
            ) : (
              <span>
                <span className={`win-chip win-${c.status}`}>
                  {WINDOW_LABELS[c.status]}
                  {c.status === 'overdue' && ` · ${plural(c.daysOverdue, 'day')}`}
                </span>
                {c.ref.nextDue && <span className="muted"> due {formatDay(c.ref.nextDue)}</span>}
                {c.lost >= 0.05 && <strong> −{lostText(c.lost)}</strong>}
              </span>
            )}
          </li>
        ))}
      </ul>

      <h4>
        Open tasks <span className="muted">({pts(shown.tasks)})</span>
      </h4>
      <p className="muted grade-pop-note">The store’s own work: tasks at this store (or assigned to its account) that are past due.</p>
      {b.tasks.length === 0 ? (
        <p className="muted">No open tasks.</p>
      ) : (
        <ul className="grade-pop-facts">
          {own.map((t) => (
            <li key={t.ref.id}>
              <span>
                <Link className="entity-link" to={`/tasks/${t.ref.id}`}>
                  {t.ref.title || 'Untitled task'}
                </Link>
                <span className="muted"> — {assignee(t.ref.assignedTo)}</span>
              </span>
              <span>
                <span className="error">Due {formatDay(t.ref.dueDate)} (overdue)</span>
                <strong> −{lostText(t.lost)}</strong>
              </span>
            </li>
          ))}
          {foreign.length > 0 && (
            <li>
              <span>
                {plural(foreign.length, 'task')} at other stores assigned to this store’s account
              </span>
              <strong>−{lostText(foreignLost)}</strong>
            </li>
          )}
          {onTimeTasks > 0 && (
            <li className="muted">
              {costly.length
                ? `+ ${plural(onTimeTasks, 'open task')} on time`
                : onTimeTasks === 1
                  ? 'The 1 open task is on time'
                  : `All ${onTimeTasks} open tasks are on time`}
            </li>
          )}
        </ul>
      )}
      <p className="grade-pop-foot muted">Quality (how the store looks when audited) is a separate grade.</p>
    </>
  );
}

/** "By whose lateness: DM audits 8 · Auditor audits 4 · Store's tasks 10" — the parts that cost points (owner ruling 2026-10-28). */
function WhoLine({ row }: { row: StoreRow }) {
  const who = lostByWho(row.breakdown);
  const parts = LOST_TO.filter((k) => who[k] > 0);
  if (parts.length === 0) return null;
  return (
    <p className="grade-pop-who">
      <span className="muted">Lost because of: </span>
      {parts.map((k, i) => (
        <span key={k}>
          {i > 0 && ' · '}
          {LOST_TO_LABEL[k]} <strong>({plural(who[k], 'point')})</strong>
        </span>
      ))}
    </p>
  );
}

/**
 * The store page's Timeliness split (owner rulings 2026-10-28: "split
 * grades by who" + "drill down to see the actual source"): points lost to
 * DM audits / Auditor audits / store-team audits / the store's own tasks,
 * each part a button that lists what's behind it — the late audit clocks
 * (form, how late, whose) or the overdue tasks.
 */
export function TimelinessSplit({ row }: { row: StoreRow }) {
  const [open, setOpen] = useState<LostTo | null>(null);
  const showsNames = useShowsNames();
  const b = row.breakdown;
  if (b.grade.timeliness === null) return null;
  const who = lostByWho(b);
  const total = LOST_TO.reduce((n, k) => n + who[k], 0);
  if (total === 0) return <p className="muted timeliness-split">Timeliness: nothing late — no points lost.</p>;
  const storeId = Number(row.store.id);
  const lateClocks = (k: LostTo) => b.clocks.filter((c) => c.party === k && c.lost > 0).sort((x, y) => y.lost - x.lost);
  const lateTasks = b.tasks.filter((t) => t.lost > 0);
  return (
    <div className="timeliness-split">
      <p>
        <span className="muted">Timeliness lost {total} point{total === 1 ? '' : 's'}: </span>
        {LOST_TO.filter((k) => who[k] > 0).map((k, i) => (
          <span key={k}>
            {i > 0 && ' · '}
            <button type="button" className="link-button" aria-expanded={open === k} onClick={() => setOpen(open === k ? null : k)}>
              {LOST_TO_LABEL[k]} {who[k]}
            </button>
          </span>
        ))}
      </p>
      {open && open !== 'tasks' && (
        <ul className="grade-pop-facts timeliness-split-list">
          {lateClocks(open).map((c, i) => (
            <li key={i}>
              <span>
                {c.ref.template.name} · {PARTY_AUDIT_LABEL[c.party]}
                <span className="muted"> — <ResponsibleName row={row} party={c.party} names={showsNames} /></span>
              </span>
              <span>
                <span className={`win-chip win-${c.status}`}>
                  {WINDOW_LABELS[c.status]}
                  {c.status === 'overdue' && ` · ${plural(c.daysOverdue, 'day')}`}
                </span>
                {c.ref.nextDue && <span className="muted"> due {formatDay(c.ref.nextDue)}</span>}
                <strong> −{lostText(c.lost)}</strong>
              </span>
            </li>
          ))}
        </ul>
      )}
      {open === 'tasks' && (
        <ul className="grade-pop-facts timeliness-split-list">
          {lateTasks.map((t) =>
            t.ref.storeId === storeId ? (
              <li key={t.ref.id}>
                <Link className="entity-link" to={`/tasks/${t.ref.id}`}>
                  {t.ref.title || 'Untitled task'}
                </Link>
                <span>
                  <span className="error">Due {formatDay(t.ref.dueDate)} (overdue)</span>
                  <strong> −{lostText(t.lost)}</strong>
                </span>
              </li>
            ) : (
              // A task at another store assigned to this account: a count, never its title (security.md "Phase 6a, Task counts").
              <li key={t.ref.id} className="muted">
                A task at another store assigned to this store’s account <strong>−{lostText(t.lost)}</strong>
              </li>
            ),
          )}
        </ul>
      )}
    </div>
  );
}

function QualityExplain({ row }: { row: StoreRow }) {
  const audits = row.breakdown.qualityAudits;
  const n = audits.length;
  return (
    <>
      <p className="grade-pop-drag">
        {n === 1
          ? 'The score of its only scored audit in the last 13 months.'
          : n < QUALITY_AUDITS
            ? `The average score of its ${n} scored audits in the last 13 months.`
            : `The average score of its last ${n} scored audits.`}
      </p>
      <ul className="grade-pop-facts">
        {audits.map((a) => (
          <li key={a.ref.id}>
            <span>
              <Link className="entity-link" to={`/audits/records/${a.ref.id}`}>
                Audit of {formatDay(a.ref.submittedAt) || 'unknown date'}
              </Link>
              {a.ref.criticalFailed && <span className="error"> · critical failure</span>}
            </span>
            <span className={`grade-chip grade-${gradeBand(a.score)}`}>{formatGrade(a.score)}</span>
          </li>
        ))}
      </ul>
      <p className="grade-pop-foot muted">
        Only audits from the last 13 months count; store-team self-audits and audits with no score
        (a form without scoring, like the DM Visit, or every question N/A) never do. Being late is the Timeliness grade.
      </p>
    </>
  );
}

const gradeTitle = (kind: GradeKind, g: number) =>
  `${GRADE_NAME[kind]} ${formatGrade(g)} (${BAND_LABEL[kind][gradeBand(g)].toLowerCase()})`;

/** A store's timeliness or quality chip → "Why this grade?". */
export function StoreGradeButton({
  row,
  kind,
  chipClass,
  label,
}: {
  row: StoreRow;
  kind: GradeKind;
  chipClass: string;
  label: string;
}) {
  const g = (kind === 'timeliness' ? row.grade.timeliness : row.grade.quality)!;
  return (
    <GradePopover
      chipClass={chipClass}
      chipText={formatGrade(g)}
      label={label}
      title={`${row.store.storeNumber} — ${row.store.name}: ${gradeTitle(kind, g)}`}
    >
      {kind === 'timeliness' ? <TimelinessExplain row={row} /> : <QualityExplain row={row} />}
    </GradePopover>
  );
}

const PULLS_SHOWN = 5;

/** A district's timeliness or quality chip → which causes and which stores pull the average down. */
export function DistrictGradeButton({
  district,
  breakdown,
  stores,
  kind,
  behind = 0,
}: {
  district: string;
  breakdown: DistrictBreakdown<StoreRow>;
  /** Stores in the district (for the "no grade yet" count). */
  stores: number;
  kind: GradeKind;
  /** Stores with an overdue audit — "8 of 13 stores overdue" beside Timeliness (owner ruling 2026-10-22). */
  behind?: number;
}) {
  const overdue = kind === 'timeliness' ? overdueStoresText(behind, stores) : null;
  const part = breakdown[kind];
  const g = part.grade!;
  const pulling = part.pulls.filter((p) => p.pull >= 0.05).slice(0, PULLS_SHOWN);
  const ungraded = stores - part.graded;
  const name = district || 'No district';
  const t = breakdown.timeliness;
  // "13 stores with a scored audit" — the plural goes on "store", not the phrase (round 2, Exec F7).
  const what = (n: number) =>
    kind === 'timeliness' ? plural(n, 'audited store') : `${n} ${n === 1 ? 'store' : 'stores'} with a scored audit`;
  return (
    <>
    <GradePopover
      chipClass={`grade-chip grade-${gradeBand(g)}`}
      chipText={formatGrade(g)}
      label={`District ${GRADE_NAME[kind].toLowerCase()} ${formatGrade(g)}, average of ${what(part.graded)}${overdue ? `; ${overdue}` : ''}`}
      title={`${name}: ${gradeTitle(kind, g)}`}
    >
      {overdue && (
        <p className="grade-pop-overdue">
          <strong>{overdue}</strong> right now — the grade averages how late each one is.
        </p>
      )}
      <p className="grade-pop-drag">
        {kind === 'timeliness'
          ? `${t.biggest ? `Mostly ${CAUSE_DRAG[t.biggest]}` : 'Nothing is making this district late'}${t.biggest && pulling[0] ? `; ${pulling[0].store.store.name} costs the most.` : '.'}`
          : pulling[0]
            ? `${pulling[0].store.store.name} has the lowest quality.`
            : 'Every store here is at 100.'}
      </p>
      <p className="muted grade-pop-note">
        Average of {what(part.graded)}
        {ungraded > 0 &&
          ` (${ungraded} ${kind === 'timeliness' ? 'not audited yet' : 'without a scored audit yet'} — no grade, no effect)`}
        {kind === 'timeliness' ? '. Points lost per part, averaged:' : '.'}
      </p>
      {kind === 'timeliness' && <LostBars lost={t.lost} weights={t.weights} grade={g} />}
      <h4>Stores pulling it down most</h4>
      {pulling.length === 0 ? (
        <p className="muted">Every store here is at 100.</p>
      ) : (
        <ul className="grade-pop-facts">
          {pulling.map((p) => (
            <li key={p.store.store.id}>
              <span>
                <StoreLink store={p.store.store} />{' '}
                <span className={`grade-chip grade-${gradeBand(p.grade)}`}>{formatGrade(p.grade)}</span>
              </span>
              <span>
                {p.biggest && <span className="muted">{CAUSE_LABEL[p.biggest]} · </span>}
                <strong>−{lostText(p.pull)}</strong>
                <span className="muted"> on the district</span>
              </span>
            </li>
          ))}
        </ul>
      )}
      {district && (
        <p className="grade-pop-foot">
          <DistrictLink district={district} /> — every store’s own breakdown is on its grade.
        </p>
      )}
    </GradePopover>
    {overdue && <span className="district-overdue">{overdue}</span>}
    </>
  );
}
